/**
 * Detect a turn that was still running when the page was reloaded.
 *
 * The chat socket streams a turn only to the connection that sent it, so a reload loses the live
 * stream while the agent keeps working. The agent persists the user row (`user-<messageId>`) when
 * the turn starts and the assistant row (`assistant-<messageId>`) only when it ends — a trailing
 * user row with no reply is therefore a turn still in flight, as long as it is recent enough to be.
 */
import type {ChatMessageRecord} from './services/chatApi'

/**
 * A turn outlives its 15-min streaming bridge (it detaches and keeps running server-side), so allow
 * long agent runs; past this, nothing can still be running.
 */
export const IN_FLIGHT_TURN_MAX_AGE_MS = 2 * 60 * 60 * 1000

const USER_ROW_PREFIX = 'user-'
const ASSISTANT_ROW_PREFIX = 'assistant-'

export interface InFlightTurn {
  /** The turn's message id — what `cancelStream` and the persisted rows are keyed by. */
  runMessageId: string
  startedAtMs: number
}

/** Id of the assistant row the agent will persist for this run, so the refetch replaces our placeholder. */
export function assistantRowIdForRun(runMessageId: string): string {
  return `${ASSISTANT_ROW_PREFIX}${runMessageId}`
}

/** Run id a persisted row belongs to (`user-<id>` / `assistant-<id>`), or null for any other id. */
export function runIdOfPersistedRow(rowId: string): string | null {
  for (const prefix of [USER_ROW_PREFIX, ASSISTANT_ROW_PREFIX]) {
    if (rowId.startsWith(prefix) && rowId.length > prefix.length) return rowId.slice(prefix.length)
  }
  return null
}

/** Epoch ms for the agent's ISO timestamps; NaN when unparseable. Trims microseconds, which not every engine accepts. */
export function parseServerTimestamp(timestamp: string): number {
  if (!timestamp) return NaN
  return Date.parse(timestamp.replace(/(\.\d{3})\d+/, '$1'))
}

export function findInFlightTurn(
  records: readonly ChatMessageRecord[],
  nowMs: number,
  abandonedRunIds?: ReadonlySet<string>,
): InFlightTurn | null {
  const last = records[records.length - 1]
  if (!last || (last.role || '').toLowerCase() !== 'user') return null
  const rowId = last.messageId ?? ''
  if (!rowId.startsWith(USER_ROW_PREFIX)) return null
  const runMessageId = rowId.slice(USER_ROW_PREFIX.length)
  if (!runMessageId || abandonedRunIds?.has(runMessageId)) return null
  const startedAtMs = parseServerTimestamp(last.timestamp)
  // A future timestamp is only clock skew between client and server, so it counts as fresh.
  if (!Number.isFinite(startedAtMs) || nowMs - startedAtMs > IN_FLIGHT_TURN_MAX_AGE_MS) return null
  return {runMessageId, startedAtMs}
}
